import { Plus, RefreshCcw, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useProjectContext } from "../../context/ProjectContext";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../ui/tooltip";
import { CreateProjectDialog } from "./CreateProjectDialog";

export function ProjectSelector() {
	const {
		projects,
		selectedProjectId,
		selectProject,
		isLoading,
		error,
		refreshProjects,
	} = useProjectContext();
	const [createOpen, setCreateOpen] = useState(false);
	const [selectOpen, setSelectOpen] = useState(false);

	const selectedProject = useMemo(
		() => projects.find((p) => p.id === selectedProjectId),
		[projects, selectedProjectId],
	);

	return (
		<div className="flex items-center gap-2">
			{isLoading ? (
				<Skeleton className="h-9 w-48" />
			) : (
				<Select
					value={selectedProjectId ?? undefined}
					onValueChange={(value) => {
						selectProject(value);
						setSelectOpen(false);
					}}
					disabled={!projects.length}
					open={selectOpen}
					onOpenChange={setSelectOpen}
				>
					<SelectTrigger className="w-48 h-9 text-sm border-input bg-background hover:bg-accent hover:text-accent-foreground focus:ring-0 focus:ring-offset-0">
						<SelectValue
							placeholder={projects.length ? "Select project" : "No projects"}
						/>
					</SelectTrigger>
					<SelectContent className="min-w-[var(--radix-select-trigger-width)]">
						{projects.map((project) => (
							<SelectItem key={project.id} value={project.id}>
								{project.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			<Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
				<Plus className="h-4 w-4 mr-1" /> Add project
			</Button>
			{error ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => refreshProjects()}
								aria-label="Retry loading projects"
							>
								<RefreshCcw className="h-4 w-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							Retry loading projects
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : null}
			{!isLoading && selectedProject ? (
				<Link href="/playground">
					<button
						type="button"
						className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors cursor-pointer border border-primary/20 whitespace-nowrap"
					>
						<Sparkles className="h-4 w-4 flex-shrink-0" />
						<span>Playground</span>
					</button>
				</Link>
			) : null}
			<CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
		</div>
	);
}
